import { useEffect, useContext, useRef } from 'react';
import { getLinkToken, getSenderInfoDetails, awaitWrap, getBESUrl, isMobileDevice } from './util';
import {
    ACTION_TYPES_LOAD_DATA, ACTION_TYPES_LOAD_MANIFEST_DATA,
    ACTION_TYPES_LOADING_MANIFEST_DATA, ACTION_TYPES_SERVICE_ERROR,
    ACTION_TYPES_UPDATE_SENDER_INFO, LinkContext,
    ACTION_TYPES_DETECT_VIEWER_LOCATION,
    ACTION_TYPES_LOAD_MICROSOFT_SCRIPT, ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES,
    ACTION_TYPES_SHOW_CUSTOM_FOOTER, ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, ACTION_TYPES_UPDATE_CONTENT_ASSETS,
    ACTION_TYPES_VALIDATE_PASSWORD, ACTION_TYPES_IS_DISABLED_GDPR, ACTION_TYPES_IS_LOAD_MANIFEST_DIRECT, ACTION_TYPES_REMEMBER_PASSWORD,
    ACTION_TYPES_ENABLED_WATERMARK
} from '../app/reducers';
import { getLinkInfo, getManifest, getSenderInfo, getLocaionInfo, getContentAssetsDownload, postPasswordValidate } from '../service/ViewerService';
import UxEventService from '../service/UxEventService';
import { ValidationErrorCode, HTTPSTATUS, DISCLAIMER_TYPE, ENABLED_WATERMARK } from './constants';
import { setLinkPwdCookie, setCompliancePwdCookie, getPasswordFromCookie, setUniqueInteractionCookie, setGDPRCookie, getTrackingStatus, resetCookieExpiredDate } from './cookieUtil';
import { isExplicitGDPR, isSkipSplashScreen, isOnlyFunctionalCookies, isGDPROn } from './gdprUtil';
import { loadScript, loadStyle } from './scriptUtil';
import { setNewrelicCustomAttribute } from './newrelicTracer';
import GDPRConsentLocation from '../model/gdprConsentLocation';
import ApplicationEntity from './appEntity';
import MicroAppPagesCollection, { buildTiledPreViewRequest } from './microappView';
import { initBesDomain, getMicroAppPagesCollection, getFeatureToggleByKey } from '../service/BuyerExperienceService';
import { canLoadManifestDirect, canLoadWatermarkManifest } from './manifestUtils';

export const useEventListener = (eventName, handler, element = window) => {
    const savedHandler = useRef();

    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);

    useEffect(
        () => {
            const isSupported = element && element.addEventListener;
            if (!isSupported) return false;

            const eventListener = event => savedHandler.current(event);

            element.addEventListener(eventName, eventListener);

            return () => {
                element.removeEventListener(eventName, eventListener);
            };
        },
        [eventName, element]
    );
};

export const initialAppAndLogEvent = () => {
    const tenant = window.seismicSDSResponse && window.seismicSDSResponse.tenantName;
    ApplicationEntity.getInstance().tenant = tenant;
    ApplicationEntity.getInstance().serviceAddress = window.seismicSDSResponse && window.seismicSDSResponse.serviceAddress;
    ApplicationEntity.getInstance().tenantUniqueId = window.seismicSDSResponse && window.seismicSDSResponse.tenantUniqueId;
    const besDomain = getBESUrl();
    initBesDomain(besDomain);
    UxEventService.appOpenEvent();
    setNewrelicCustomAttribute('Tenant', tenant);
};

const setIconAndTitleForViewer = (data) => {
    if (data.viewerSettings && data.viewerSettings.browserSetting) {
        const { browserSetting } = data.viewerSettings;
        const { title, favicon } = browserSetting;
        document.getElementsByTagName('title')[0].innerText = title || 'liveSend viewer';
        const iconElement = document.createElement('link');
        iconElement.rel = 'icon';
        iconElement.href = favicon || '';
        document.getElementsByTagName('head')[0].appendChild(iconElement);
    }
};

const dispatchLatestSenderInfo = async (linkToken, dispatch, linkInfo) => {
    const senderInfoPromise = getSenderInfo(linkToken);
    const [err, data] = await awaitWrap(senderInfoPromise);

    if (data) {
        dispatch({ type: ACTION_TYPES_UPDATE_SENDER_INFO, payload: getSenderInfoDetails(data, linkInfo.tenant) });
    } else if (err) {
        if (linkInfo.link && linkInfo.link.sender) {
            dispatch({ type: ACTION_TYPES_UPDATE_SENDER_INFO, payload: linkInfo.link.sender });
        }
    }
};

const dispatchViewerLocation = async (dispatch) => {
    const getLocation = getLocaionInfo();
    const [err, data] = await awaitWrap(getLocation);
    if (data) {
        dispatch({ type: ACTION_TYPES_DETECT_VIEWER_LOCATION, payload: data });
    } else if (err) {
        console.error(err);
    }

    return data;
};

const loadMicroAppPages = async (contents, linkToken, interactionId) => {
    const tenant = ApplicationEntity.getInstance().tenant;
    const microAppContents = buildTiledPreViewRequest(contents);
    if (microAppContents && microAppContents.length > 0) {
        try {
            const tiledPreviewPages = await getMicroAppPagesCollection(interactionId || linkToken, microAppContents, tenant);
            if (tiledPreviewPages && Array.isArray(tiledPreviewPages)) {
                MicroAppPagesCollection.getInstance(tiledPreviewPages);
            }
        } catch (error) {
            console.log(error);
        }
    }
};

const dispatchManifestData = async (dispatch, {
    linkToken,
    Password = '',
    CompliancePassword = '',
    consentLocation = {},
    trackingType
}) => {
    dispatch({ type: ACTION_TYPES_LOADING_MANIFEST_DATA, payload: true });
    const manifestPromise = getManifest(linkToken, { Password, CompliancePassword }, trackingType);
    const [err, data] = await awaitWrap(manifestPromise);

    if (data) {
        if (data.Code === ValidationErrorCode.LIVESEND_MANIFEST_OK) {
            // Add reducer to handle seller preview cannot remember password in cookie
            dispatch({
                type: ACTION_TYPES_REMEMBER_PASSWORD,
                payload: {
                    Password,
                    CompliancePassword
                }
            });
            // remember successful login
            if (Password) setLinkPwdCookie(Password);
            if (CompliancePassword) setCompliancePwdCookie(CompliancePassword);
            if (isExplicitGDPR(consentLocation)) {
                setUniqueInteractionCookie();
                setGDPRCookie();
                dispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
            }
        }

        dispatch({ type: ACTION_TYPES_LOAD_MANIFEST_DATA, payload: data });
    } else if (err) {
        dispatch({ type: ACTION_TYPES_SERVICE_ERROR, payload: err });
    }

    return data;
};

const loadMicrosoteGDPR = async (gdprSetting, dispatch) => {
    if (isExplicitGDPR(gdprSetting && gdprSetting.consentLocation)) {
        dispatch({ type: ACTION_TYPES_LOAD_MICROSOFT_SCRIPT, payload: true });
        dispatch({ type: ACTION_TYPES_SHOW_CUSTOM_FOOTER, payload: true });
        if (gdprSetting.microsoftDisclaimerMarkup) {
            loadStyle(gdprSetting.microsoftDisclaimerMarkup.GDPRStyle);
            const loadMSScript = loadScript(gdprSetting.microsoftDisclaimerMarkup.GDPRScript);
            const [loadError, hasLoaded] = await awaitWrap(loadMSScript);
            dispatch({ type: ACTION_TYPES_LOAD_MICROSOFT_SCRIPT, payload: false });
            if (loadError) {
                return false;
            }

            if (hasLoaded) {
                return true;
            }
        }
    }

    return false;
};

const initialLD = async (dispatch) => {
    const tenantUniqueId = ApplicationEntity.getInstance().tenantUniqueId;
    const isWatermarkEnabled = await getFeatureToggleByKey(tenantUniqueId, ENABLED_WATERMARK);

    dispatch({
        type: ACTION_TYPES_ENABLED_WATERMARK,
        payload: isWatermarkEnabled
    });

    return { isWatermarkEnabled };
};

export const useInitApp = () => {
    useEffect(() => {
        initialAppAndLogEvent();
        // Reset expired date for cookies when init app
        resetCookieExpiredDate();
    }, []);
};

const initialViewer = async (linkToken, dispatch, humanEvent) => {
    const initLDPromise = initialLD(dispatch);
    const linkInfoPromise = getLinkInfo(linkToken);
    const [, toggle] = await awaitWrap(initLDPromise);
    const [err, data] = await awaitWrap(linkInfoPromise);
    const { isWatermarkEnabled } = toggle;

    if (data) {
        setIconAndTitleForViewer(data);
        // const tenantUniqueId = ApplicationEntity.getInstance().tenantUniqueId;
        // eslint-disable-next-line no-unused-vars
        // Will retire this LD toggle since we don't this toggle for any tenant

        // As the comments in https://seismic.atlassian.net/browse/LS-2166, we prefer to show the 500 error page when there is no contents in a link
        if (!data.link || !data.link.contents || data.link.contents.length === 0) {
            dispatch({ type: ACTION_TYPES_SERVICE_ERROR, payload: { errorCode: HTTPSTATUS.INTERNAL_SERVER_ERROR } });
            return;
        }

        const { isPasswordRequired, isExpired, isCompliancePasswordRequired, revokedAt } = data.viewerSettings;
        // LS-2505 Microsites - Viewer experience for "revoked" link
        // As the requirement in https://seismic.atlassian.net/wiki/spaces/LS/pages/1075848205/Microsites#Microsites-Phase2-IntroductionoftheEngagementCenter
        // the revoked link shows exactly as the 404 error page
        if (revokedAt) {
            dispatch({ type: ACTION_TYPES_SERVICE_ERROR, payload: { errorCode: HTTPSTATUS.NOT_FOUNT } });
            return;
        }

        let viewerLocation = null;
        if (data.gdprSetting && data.gdprSetting.consentLocation === GDPRConsentLocation.PARTIAL) {
            viewerLocation = await dispatchViewerLocation(dispatch);
        }

        const isLoadScriptSuccess = await loadMicrosoteGDPR(data.gdprSetting, dispatch);

        // load microApp preview pages for order tiled page index
        const { link, gdprSetting, privacySetting } = data;
        const { isWatermarkingRequired, contents, interactionId } = link;
        await loadMicroAppPages(contents, linkToken, interactionId);

        const { password, compliancePassword } = getPasswordFromCookie(isPasswordRequired, isExpired, isCompliancePasswordRequired);
        // dispatch all the data after we have built all infos needed
        const skipSplashScreen = isSkipSplashScreen(data.gdprSetting, isLoadScriptSuccess, isPasswordRequired, isExpired, viewerLocation, data.privacySetting, humanEvent, isWatermarkEnabled);
        const isNoneDisclaimer = privacySetting && privacySetting.isEnable && privacySetting.currentCountry.disclaimerLibrary.type === DISCLAIMER_TYPE.NONE_DISCLAIMER;
        const isDisabledGDPR = !isGDPROn(gdprSetting, viewerLocation, privacySetting) || isNoneDisclaimer;
        const willLoadManifestDirect = canLoadManifestDirect(isDisabledGDPR, isExpired, isPasswordRequired, isWatermarkingRequired, !!password, !!compliancePassword, isWatermarkEnabled);

        if (!isDisabledGDPR) {
            dispatch({
                type: ACTION_TYPES_IS_ONLY_FUNCTIONAL_COOKIES,
                payload: isOnlyFunctionalCookies(isWatermarkEnabled)
            });
        }

        if (willLoadManifestDirect) {
            dispatch({ type: ACTION_TYPES_IS_LOAD_MANIFEST_DIRECT, payload: true });
        }

        dispatch({ type: ACTION_TYPES_IS_DISABLED_GDPR, payload: isDisabledGDPR });
        dispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: skipSplashScreen });
        dispatch({ type: ACTION_TYPES_LOAD_DATA, payload: data });
        dispatchLatestSenderInfo(linkToken, dispatch, data);
        // Enhancement getmanifest request
        if (willLoadManifestDirect) {
            const trackingType = getTrackingStatus(isWatermarkEnabled, isDisabledGDPR, !!compliancePassword, true);
            dispatchManifestData(dispatch, {
                linkToken,
                Password: password,
                CompliancePassword: compliancePassword,
                trackingType
            });
        }
    } else if (err) {
        dispatch({ type: ACTION_TYPES_SERVICE_ERROR, payload: err });
    }
};

export const useLoadLinkInfo = (humanEvent) => {
    const { state, dispatch } = useContext(LinkContext);
    const linkToken = getLinkToken();

    useEffect(() => {
        initialViewer(linkToken, dispatch, humanEvent);
    }, [linkToken]);

    return { state, dispatch };
};

export const useLoadManifest = (password = '', compliancePassword = '', watch) => {
    const { state, dispatch } = useContext(LinkContext);
    const linkToken = getLinkToken();
    const { linkBasicInfo, gdprSetting, isDisabledGDPR, isWatermarkEnabled } = state;
    const { isWatermarkingRequired } = linkBasicInfo;
    const { consentLocation } = gdprSetting;

    useEffect(() => {
        if (!password && !compliancePassword) return;
        if (!isWatermarkingRequired) {
            const trackingType = getTrackingStatus(isWatermarkEnabled, isDisabledGDPR, !!compliancePassword, true);
            dispatchManifestData(dispatch, {
                linkToken,
                Password: password,
                CompliancePassword: compliancePassword,
                consentLocation,
                trackingType
            });
        }
    }, [watch]);

    return state;
};

export const useInitViewPort = () => {
    useEffect(() => {
        if (isMobileDevice.any()) {
            const metaViewport = document.querySelector('meta[name=viewport]');
            metaViewport.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=no');
        }
    }, []);
};

export const useLoadDownloadAssets = () => {
    const { state, dispatch } = useContext(LinkContext);
    const { linkBasicInfo, isDisabledGDPR, passwords, downloadAssets, isWatermarkEnabled } = state;
    const { canDownload, enableLiveSendDownloadOptions, isPasswordRequired, isExpired, isCompliancePasswordRequired } = linkBasicInfo;

    const getDownloadAssets = async () => {
        const { password, compliancePassword } = getPasswordFromCookie(isPasswordRequired, isExpired, isCompliancePasswordRequired);
        const { Password, CompliancePassword } = passwords;
        const downloadURLPromise = getContentAssetsDownload({
            trackingType: getTrackingStatus(isWatermarkEnabled, isDisabledGDPR, !!compliancePassword),
            linkToken: getLinkToken(),
            body: { Password: password || Password, CompliancePassword: compliancePassword || CompliancePassword }
        });

        const [err, data] = await awaitWrap(downloadURLPromise);

        if ((data && (data.code || data.Code)) || err) {
            dispatch({ type: ACTION_TYPES_UPDATE_CONTENT_ASSETS, payload: [] });
            return;
        }

        if (data && data.length > 0) {
            dispatch({ type: ACTION_TYPES_UPDATE_CONTENT_ASSETS, payload: data });
        }
    };

    useEffect(() => {
        // optimize opt in again from watermark error page
        if (downloadAssets && downloadAssets.length > 0) return;
        if (enableLiveSendDownloadOptions && canDownload && !isExpired) {
            getDownloadAssets();
        }
    }, []);
};

const validatePassword = async (password = '', compliancePassword = '', linkToken, consentLocation, dispatch) => {
    dispatch({ type: ACTION_TYPES_LOADING_MANIFEST_DATA, payload: true });
    const requestData = { linkToken, password, compliancePassword };
    const linkInfoPromise = postPasswordValidate(requestData);
    const [err, data] = await awaitWrap(linkInfoPromise);

    if (data) {
        if (!data.Code) {
            // remember successful login
            if (password) setLinkPwdCookie(password);
            if (compliancePassword) setCompliancePwdCookie(compliancePassword);
            // Add reducer to handle seller preview cannot remember password in cookie
            if (isExplicitGDPR(consentLocation)) {
                setUniqueInteractionCookie();
                setGDPRCookie();
                dispatch({ type: ACTION_TYPES_IS_SKIP_SPLASH_SCREEN, payload: true });
            }

            const payload = {
                errorCode: ValidationErrorCode.LIVESEND_MANIFEST_OK,
                errorMessage: data.Message,
                isLoadingManifest: false
            };

            if (compliancePassword) {
                payload.isValidatedCompliancePassword = true;
            }

            dispatch({ type: ACTION_TYPES_VALIDATE_PASSWORD, payload });
            return;
        }

        dispatch({
            type: ACTION_TYPES_VALIDATE_PASSWORD,
            payload: {
                errorCode: data.Code,
                errorMessage: data.Message,
                isLoadingManifest: false
            }
        });
    } else if (err) {
        dispatch({ type: ACTION_TYPES_SERVICE_ERROR, payload: err });
    }
};

export const useValidatePassword = (password = '', compliancePassword = '', watch) => {
    const { state, dispatch } = useContext(LinkContext);
    const linkToken = getLinkToken();
    const { linkBasicInfo, gdprSetting } = state;
    const { isWatermarkingRequired } = linkBasicInfo;
    const { consentLocation } = gdprSetting;

    useEffect(() => {
        if (!password && !compliancePassword) return;
        if (isWatermarkingRequired) {
            dispatch({
                type: ACTION_TYPES_REMEMBER_PASSWORD,
                payload: {
                    Password: password,
                    CompliancePassword: compliancePassword
                }
            });
            validatePassword(password, compliancePassword, linkToken, consentLocation, dispatch);
        }
    }, [watch]);

    return state;
};

export const useLoadWatermarkManifest = () => {
    const { state, dispatch } = useContext(LinkContext);
    const linkToken = getLinkToken();
    const { linkBasicInfo, isValidatedCompliancePassword, isDisabledGDPR, isLoadManifestDirect, passwords, manifests, isWatermarkEnabled } = state;
    const { isPasswordRequired, isExpired, isCompliancePasswordRequired, isWatermarkingRequired } = linkBasicInfo;
    const { password, compliancePassword } = getPasswordFromCookie(isPasswordRequired, isExpired, isCompliancePasswordRequired);
    const { Password, CompliancePassword } = passwords;

    useEffect(() => {
        // optimize opt in again from watermark error page
        if (manifests && manifests.length > 0) return;
        if (canLoadWatermarkManifest(isWatermarkingRequired, isExpired, isValidatedCompliancePassword, isLoadManifestDirect, isWatermarkEnabled, isDisabledGDPR)) {
            const trackingType = getTrackingStatus(isWatermarkEnabled, isDisabledGDPR, !!compliancePassword);
            dispatchManifestData(dispatch, {
                linkToken,
                Password: password || Password,
                CompliancePassword: compliancePassword || CompliancePassword,
                consentLocation: state.gdprSetting.consentLocation,
                trackingType
            });
        }
    }, [isValidatedCompliancePassword]);
};
